/**
 * Le lecteur visite-t-il une spécialité qui n'est pas la sienne ? (PT-74)
 *
 * An admin picks any speciality from the sidebar, a MAGE-X reviewer those an admin granted them
 * (PT-137); any other session that would visit one only reads there, and the backend refuses its
 * mutating requests. This hook is what keeps the interface honest about it — a button that answers
 * 403 is worse than a button that is not there.
 *
 * It mirrors the backend rule — a chosen speciality the account does not write in — with one
 * addition: the SIMULATED role decides, not the real one. An admin who puts the eye on « global »
 * while a speciality is chosen is asking what a visitor would see, and a visitor sees no pencil.
 * The eye only ever narrows what is displayed, so nothing is granted by this and the admin's own
 * requests are still honoured the moment they drop the preview.
 *
 * Two things it is deliberately NOT:
 *
 *  - It is not about « Vue médecin ». Previewing a DOCTOR is an identity preview with a perimeter
 *    and a banner of its own; borrowing a speciality is a different question and answering both
 *    here would stack two banners saying different things about the same screen.
 *  - It is not `specialiteIsBorrowed`, which highlights the sidebar for an admin too. An admin
 *    reading as themselves keeps their hands: their perimeter is the platform.
 *
 * Where the account writes is `useWritableSpecialites`: the API's own list, its own speciality
 * first (resolved through the registry — a reviewer whose row is NULL belongs to the speciality that
 * absorbs unassigned accounts), then a reviewer's grants.
 */
import { usePreviewView } from "@app/zustand/preview-view";
import useUser from "@app/zustand/user";
import { useWritableSpecialites } from "@app/utils/use-writable-specialites";

export function useReadonlyScope(): boolean {
  const user = useUser((state) => state.user);
  const previewRole = usePreviewView((state) => state.previewRole);
  const previewSpecialite = usePreviewView((state) => state.previewSpecialite);
  const writable = useWritableSpecialites();
  const account = user as { role?: string } | null;
  if (!account) return false;
  if (previewRole === "medecin") return false;
  const effectiveRole = previewRole ?? account.role;
  if (effectiveRole === "admin") return false;
  return !!previewSpecialite && !writable.includes(previewSpecialite);
}
